"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { SectionHeader } from "@/components/layout";
import type { BaseCardMetaMode, BaseWithRelations } from "@/types/base";

import BaseGrid from "../BaseGrid";
import BaseRelatedDisplayAd from "./BaseRelatedDisplayAd";

interface Props {
  title: string;
  bases: BaseWithRelations[];
  showAds?: boolean;
  adSlot?: string;
  adContextKey?: string;
  showMeta?: BaseCardMetaMode;
}

function getSplitIndex(viewportWidth: number) {
  if (viewportWidth >= 1024) return 4;
  if (viewportWidth >= 768) return 3;
  return 2;
}

export default function RelatedBasesIslandClient({
  title,
  bases,
  showAds = false,
  adSlot,
  adContextKey,
  showMeta = false,
}: Props) {
  const rootRef = useRef<HTMLElement | null>(null);
  const [showImages, setShowImages] = useState(false);
  const [viewportWidth, setViewportWidth] = useState<number>(1440);

  useEffect(() => {
    const node = rootRef.current;

    if (!node || showImages) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry?.isIntersecting) {
          setShowImages(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "300px 0px",
      }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [showImages]);

  useEffect(() => {
    const updateViewportWidth = () => {
      setViewportWidth(window.innerWidth || 1440);
    };

    updateViewportWidth();
    window.addEventListener("resize", updateViewportWidth, { passive: true });

    return () => {
      window.removeEventListener("resize", updateViewportWidth);
    };
  }, []);

  const splitIndex = useMemo(
    () => getSplitIndex(viewportWidth),
    [viewportWidth]
  );

  const canSplitForAd = showAds && bases.length > splitIndex;
  const primaryBases = canSplitForAd ? bases.slice(0, splitIndex) : bases;
  const remainingBases = canSplitForAd ? bases.slice(splitIndex) : [];

  const shouldRenderAd =
    canSplitForAd &&
    Boolean(adSlot) &&
    Boolean(adContextKey) &&
    remainingBases.length > 0;

  return (
    <section ref={rootRef}>
      <div className="px-lg">
        <div className="lane">
          <div className="py-sm">
            <SectionHeader title={title} />

            <BaseGrid
              bases={primaryBases}
              showImage={showImages}
              mobileBleed={false}
              showMeta={showMeta}
            />
          </div>
        </div>
      </div>

      {shouldRenderAd ? (
        <div data-sticky-ad-trigger="true">
          <BaseRelatedDisplayAd slot={adSlot!} />
        </div>
      ) : null}

      {remainingBases.length > 0 ? (
        <div className="px-lg">
          <div className="lane">
            <div className="py-sm">
              <BaseGrid
                bases={remainingBases}
                showTime
                showImage={showImages}
                mobileBleed={false}
                showMeta={showMeta}
              />
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}